/**
 * Recipe Fetch Client
 *
 * Thin fetch client for retrieving recipe index and individual recipe files
 * from the static assets served via S3/CloudFront.
 */

const TIMEOUT_MS = 10000;

/**
 * Custom error class for fetch failures with HTTP status information.
 */
export class FetchError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'FetchError';
    this.status = status; // HTTP status code or null for network errors
  }
}

/**
 * Creates a timeout signal for fetch requests.
 * Uses AbortSignal.timeout when available, falls back to AbortController.
 * @param {number} ms - Timeout in milliseconds
 * @returns {AbortSignal}
 */
function createTimeoutSignal(ms) {
  if (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

/**
 * Fetches the recipe index JSON from /recipes/index.json.
 * @returns {Promise<{categories: Array}>} Parsed index object
 * @throws {FetchError} On network error, non-200 status, JSON parse failure, or timeout
 */
export async function fetchRecipeIndex() {
  let response;

  try {
    response = await fetch('/recipes/index.json', {
      signal: createTimeoutSignal(TIMEOUT_MS),
    });
  } catch (err) {
    if (err.name === 'TimeoutError' || err.name === 'AbortError') {
      throw new FetchError('Request timed out while fetching recipe index', null);
    }
    throw new FetchError('Network error while fetching recipe index', null);
  }

  if (!response.ok) {
    throw new FetchError(
      `Failed to fetch recipe index: HTTP ${response.status}`,
      response.status
    );
  }

  try {
    return await response.json();
  } catch (err) {
    throw new FetchError('Failed to parse recipe index JSON', null);
  }
}

/**
 * Fetches a single recipe markdown file.
 * @param {string} category - Category folder name
 * @param {string} slug - Recipe slug (filename without .md)
 * @returns {Promise<string>} Raw markdown content
 * @throws {FetchError} With status property (404 for not found, other for errors)
 */
export async function fetchRecipeFile(category, slug) {
  let response;

  try {
    response = await fetch(`/recipes/${category}/${slug}.md`, {
      signal: createTimeoutSignal(TIMEOUT_MS),
    });
  } catch (err) {
    if (err.name === 'TimeoutError' || err.name === 'AbortError') {
      throw new FetchError('Request timed out while fetching recipe', null);
    }
    throw new FetchError('Network error while fetching recipe', null);
  }

  if (response.status === 404) {
    throw new FetchError('Recipe not found', 404);
  }

  if (!response.ok) {
    throw new FetchError(
      `Failed to fetch recipe: HTTP ${response.status}`,
      response.status
    );
  }

  return await response.text();
}
